import { PrevNextButtons } from '@app/core/components/PrevNextButtons';
import { usePostHogCaptureLiveTopics } from '@app/modules/logger/screens/stream/features/live-topics/hooks/usePostHogCaptureLiveTopics';
import { LiveTopic } from '../../live-topics-types';
import { useCallback } from 'react';

/**
 * Prev/next topic navigation in the detail view. Handles index lookup, older/newer topic resolution,
 * and PostHog capture when navigating; calls onSelectTopic to change the selected topic.
 */
export function LiveTopicNavigation({
  topic,
  topics,
  onSelectTopic,
}: {
  topic: LiveTopic;
  topics: LiveTopic[];
  onSelectTopic: (id: string) => void;
}) {
  const capture = usePostHogCaptureLiveTopics();

  const index = topics.findIndex((t) => t.id === topic.id);
  const hasOlder = index >= 0 && index < topics.length - 1;
  const hasNewer = index > 0;
  const olderTopic = hasOlder ? topics[index + 1] : null;
  const newerTopic = hasNewer ? topics[index - 1] : null;

  const handlePrev = useCallback(() => {
    if (newerTopic) {
      capture('detail_navigated', {
        direction: 'prev',
        from_topic_id: topic.id,
        to_topic_id: newerTopic.id,
      });
      onSelectTopic(newerTopic.id);
    }
  }, [topic.id, newerTopic, onSelectTopic, capture]);

  const handleNext = useCallback(() => {
    if (olderTopic) {
      capture('detail_navigated', {
        direction: 'next',
        from_topic_id: topic.id,
        to_topic_id: olderTopic.id,
      });
      onSelectTopic(olderTopic.id);
    }
  }, [topic.id, olderTopic, onSelectTopic, capture]);

  return (
    <PrevNextButtons
      orientation="vertical"
      color="secondary"
      onPrev={handlePrev}
      onNext={handleNext}
      hasPrev={hasNewer}
      hasNext={hasOlder}
    />
  );
}
